import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Button } from '@trove/ui';
import { ArrowLeft, CaretRight, DeviceMobile, Fingerprint, Password } from '@phosphor-icons/react';
import { getRepositories } from '../api';
import { passkeysSupported, unlockVaultWithPasskey } from '../passkeys';
import { useSession } from '../session';
import { useWorkspace } from '../workspace';
import shared from '../styles/shared.module.css';
import { EntryActions, EntryLayout, EntryNotice } from './EntryLayout';
import { PairDevice } from './PairDevice';
import { BackupPhraseStep, RestorePhraseForm } from './VaultSetup';
import styles from './SetupGate.module.css';

export type SetupView = 'choose' | 'pair' | 'phrase';

/**
 * Shown when the signed-in account has no vault unlocked in this browser: either create the
 * first vault, or unlock an existing one with a passkey, another device, or the backup phrase.
 */
export function SetupGate({ initialView = 'choose' }: { initialView?: SetupView }) {
  const { user } = useSession();
  const { refresh } = useWorkspace();
  const navigate = useNavigate();
  const [repositoryExists, setRepositoryExists] = useState<boolean | null>(null);
  const [error, setError] = useState<string>();
  const [view, setView] = useState<SetupView>(initialView);
  const [pairingKey, setPairingKey] = useState(0);
  const [unlockError, setUnlockError] = useState<string>();
  const [passkeyPending, setPasskeyPending] = useState(false);
  const isRecipient = user?.displayName === 'Alex';
  const showPasskey = passkeysSupported() && !user?.demo;

  useEffect(() => {
    if (isRecipient) return;
    void getRepositories()
      .then((result) => setRepositoryExists(result.repositories.length > 0))
      .catch((cause) =>
        setError(cause instanceof Error ? cause.message : 'Could not check your account'),
      );
  }, [isRecipient]);

  const goHome = () => void navigate({ to: '/', search: {} });

  const unlockWithPasskey = async () => {
    setUnlockError(undefined);
    setPasskeyPending(true);
    try {
      const result = await unlockVaultWithPasskey();
      if (result.unlockedVaults === 0) {
        throw new Error(
          'This passkey can sign you in but can’t unlock your files. Pair with another device or use your backup phrase.',
        );
      }
      await refresh();
    } catch (cause) {
      if (!(cause instanceof Error && cause.name === 'NotAllowedError')) {
        setUnlockError(cause instanceof Error ? cause.message : 'Could not unlock with passkey');
      }
    } finally {
      setPasskeyPending(false);
    }
  };

  if (isRecipient) {
    return (
      <div className={styles.recipient}>
        <p className={shared.empty}>No shared collection is open.</p>
        <Button variant="primary" onClick={() => void navigate({ to: '/shared' })}>
          View invitations
        </Button>
      </div>
    );
  }

  if (error) {
    return (
      <EntryLayout title="Couldn’t check your account" description={error}>
        <EntryActions>
          <Button variant="primary" onClick={() => window.location.reload()}>
            Try again
          </Button>
        </EntryActions>
      </EntryLayout>
    );
  }

  if (repositoryExists === null) {
    return (
      <div className={shared.splash}>
        <div className={shared.splashInner}>
          <div className={shared.spinner} />
          Checking your account…
        </div>
      </div>
    );
  }

  if (!repositoryExists) {
    return (
      <EntryLayout
        title="Save your backup phrase"
        description="Your trove is created with these 12 words. They’re the only way back into your files if you lose this device — Trove can’t reset them."
      >
        <BackupPhraseStep onCreated={goHome} />
      </EntryLayout>
    );
  }

  const back = (
    <button
      type="button"
      className={styles.back}
      onClick={() => {
        setView('choose');
        setUnlockError(undefined);
      }}
    >
      <ArrowLeft size={14} />
      Other ways to unlock
    </button>
  );

  if (view === 'pair') {
    return (
      <EntryLayout
        eyebrow={back}
        title="Approve from another device"
        description="On a device where Trove is already open, scan this code or enter it at Connect device."
      >
        <PairDevice
          key={pairingKey}
          onUnlocked={goHome}
          onRetry={() => setPairingKey((value) => value + 1)}
        />
      </EntryLayout>
    );
  }

  if (view === 'phrase') {
    return (
      <EntryLayout
        eyebrow={back}
        title="Enter your backup phrase"
        description="The 12 words you saved when you created your trove."
      >
        <RestorePhraseForm onUnlocked={goHome} />
      </EntryLayout>
    );
  }

  return (
    <EntryLayout
      title="Unlock your files on this device"
      description="Choose how to unlock Trove in this browser. You’ll only need to do this once."
    >
      <div className={styles.options}>
        {showPasskey ? (
          <UnlockOption
            icon={<Fingerprint size={18} />}
            title={passkeyPending ? 'Waiting for passkey…' : 'Use a passkey'}
            description="Face ID, Touch ID, or a security key"
            disabled={passkeyPending}
            onClick={() => void unlockWithPasskey()}
          />
        ) : null}
        <UnlockOption
          icon={<DeviceMobile size={18} />}
          title="Approve from another device"
          description="Scan a code with a phone or computer that’s already unlocked"
          onClick={() => setView('pair')}
        />
        <UnlockOption
          icon={<Password size={18} />}
          title="Enter backup phrase"
          description="The 12 words you saved during setup"
          onClick={() => setView('phrase')}
        />
      </div>
      {unlockError ? <EntryNotice tone="danger">{unlockError}</EntryNotice> : null}
    </EntryLayout>
  );
}

function UnlockOption({
  icon,
  title,
  description,
  disabled,
  onClick,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button type="button" className={styles.option} disabled={disabled} onClick={onClick}>
      <span className={styles.optionIcon}>{icon}</span>
      <span className={styles.optionText}>
        <span className={styles.optionTitle}>{title}</span>
        <span className={styles.optionDescription}>{description}</span>
      </span>
      <CaretRight size={14} className={styles.optionCaret} />
    </button>
  );
}
